// Type definitions and label constants for the AML feature.
// Mirrors the corresponding shapes in sprive-backend:
//   - `AmlMatchReason`              -> src/helpers/equifax/aml.ts
//   - `AmlSummaryFlagKey`           -> src/helpers/equifax/aml.ts
//   - `WatchlistFullReport`         -> src/helpers/equifax/watchlist-full-report.ts
//   - `WatchlistRelativeAssociate`  -> src/helpers/equifax/watchlist-full-report.ts
//   - `WatchlistRoleData`           -> src/helpers/equifax/watchlist-full-report.ts
//   - `WatchlistAddress`            -> src/helpers/equifax/watchlist-full-report.ts
//   - `WatchlistCategorisation`     -> src/helpers/equifax/watchlist-full-report.ts
//   - `CoolingOffStatus`            -> src/helpers/equifax/watchlist-full-report.ts
// Keep these in sync if the backend interfaces change.

// ---------------------------------------------------------------------------
// `/admin/aml-verification-pps` action discriminator.
// Mirrors the action branches in
//   `sprive-backend/src/services/admin/aml-verification-pps/aml-verification-pps.class.ts`.
// Lives here (not in apiConstants) because it's an AML-feature concern; the
// global apiConstants module is reserved for URL paths.
// ---------------------------------------------------------------------------

export const AML_VERIFICATION_PPS_ACTIONS = {
    CHECK: "check",
    BULK_CHECK: "bulk-check",
    DOWNLOAD: "download",
    BULK_DOWNLOAD: "bulk-download",
    WATCHLIST_DETAILS: "watchlist-details",
    // Re-runs the full Equifax AML check for one user from scratch. Backend
    // inserts a new `aml_verification` row, updates `user.aml_verification`,
    // and auto-prefetches Watchlist Full Reports for any new true matches.
    // Original rows are kept for audit. One paid Equifax call per invocation.
    REFRESH_AML: "refresh-aml"
} as const;

export type AmlVerificationPpsAction =
    typeof AML_VERIFICATION_PPS_ACTIONS[keyof typeof AML_VERIFICATION_PPS_ACTIONS];

// ---------------------------------------------------------------------------
// AML summary flag keys (mirrors backend `AmlSummaryFlagKey`).
// ---------------------------------------------------------------------------

export type AmlSummaryFlagKey =
    | 'sanctions'
    | 'highRiskPep'
    | 'standardRiskPep'
    | 'highRiskSip'
    | 'standardRiskSip'
    | 'highRiskRca'
    | 'standardRiskRca';

// Subset of summary flags whose presence causes onboarding AML to FAIL.
// Mirrors backend `AML_FAILURE_FLAGS`. Useful here for highlighting cells in
// the admin grid that map to a true failure vs. an informational flag.
export const AML_FAILURE_FLAGS: AmlSummaryFlagKey[] = [
    'sanctions',
    'highRiskSip',
    'standardRiskSip'
];

// ---------------------------------------------------------------------------
// AML match reason (one per <watchlistEntry> in the AML response).
// Mirrors backend `AmlMatchReason`.
// ---------------------------------------------------------------------------

export interface AmlMatchReason {
    matchCategory: string;
    checkName: string;
    percentageMatch: string;
    name: string;
    relatedName: string;
    jurisdiction: string;
    citizenship: string;
    alertIdentifier: string;
    matchIdentifier: string;
    transactionReference: string;
}

// ---------------------------------------------------------------------------
// Equifax Watchlist Full Report (Dow Jones EDD).
// Mirrors backend `WatchlistFullReport` and its sub-types.
// ---------------------------------------------------------------------------

export interface WatchlistRelativeAssociate {
    associationType: string;
    association: string;
    associationName: string;
}

export interface WatchlistRoleData {
    roleType: string;
    role: string;
}

export interface WatchlistAddress {
    addressLine1?: string;
    city?: string;
    region?: string;
    postcode?: string;
    country?: string;
    raw?: string;
}

export interface WatchlistCategorisation {
    categoryType: string;
    category: string;
}

export interface CoolingOffStatus {
    inactiveAsOfDate: string | null;
    inactiveAsOfType: string | null;
    coolingOffEndsOn: string | null;
    pastCoolingOff: boolean | null;
    daysSinceInactive: number | null;
}

export interface WatchlistFullReport {
    clientRef: string;
    enquiryReference: string | null;

    matchedName: string;
    gender: string;
    category: string;
    categoryType: string;
    categorisations: WatchlistCategorisation[];

    djDob: string;
    placeOfBirth: string;

    imageUrls: string[];

    citizenship: string;
    jurisdiction: string;
    residentOf: string;

    djUniqueIdentifier: string;
    djActiveStatus: string;
    djCreateDate: string;
    djLastUpdatedDate: string;
    djDeceased: string;

    inactiveAsOfDate: string | null;
    inactiveAsOfType: string | null;

    relativesAndAssociates: WatchlistRelativeAssociate[];
    roles: WatchlistRoleData[];
    addresses: WatchlistAddress[];

    coolingOff: CoolingOffStatus;

    // Added by backend when serving the report from the on-disk cache rather
    // than calling Equifax fresh.
    cached?: boolean;
}

// One per AML match: the original reason plus (optionally) its hydrated
// Dow Jones Full Report. Mirrors backend `AmlMatchWithFullReport`.
export interface AmlMatchWithFullReport extends AmlMatchReason {
    full_report: WatchlistFullReport | null;
    full_report_error?: string;
}

// ---------------------------------------------------------------------------
// AML check result row (what `/admin/aml-verification-pps` returns per user).
// ---------------------------------------------------------------------------

export interface AmlCheckResult {
    date: string;
    pps_account_number: string;
    email: string;
    full_name: string;
    dob: string;
    address: string;

    aml_flags: string;

    // SP-187: enriched fields.
    match_details: string;
    match_reasons: AmlMatchReason[];
    watchlist_reports: AmlMatchWithFullReport[];
    watchlist_summary: string;

    // Raw per-flag booleans (string 'true' / 'false' / 'N/A').
    sanctions: string;
    highRiskPep: string;
    standardRiskPep: string;
    highRiskSip: string;
    standardRiskSip: string;
    highRiskRca: string;
    standardRiskRca: string;

    vulnerable: string;
}

// ---------------------------------------------------------------------------
// Display labels.
// Mirrors backend `ADMIN_CONSTANTS.ADMIN_AML_CHECK_RESULTS`. Property order
// matches AML XLSX export columns: identity fields, then match + watchlist.
// ---------------------------------------------------------------------------

export const ADMIN_AML_CHECK_RESULTS = {
    DATE: "Date",
    PPS_ACCOUNT_NUMBER: "PPS Account Number",
    EMAIL: "Email",
    AML_FLAGS: "AML Flags",
    FULL_NAME: "Full Name",
    DOB: "Date of Birth",
    ADDRESS: "Address",
    MATCH_DETAILS: "Match Details",
    WATCHLIST_MATCHED_NAME: "Watchlist Matched Name",
    WATCHLIST_CATEGORY: "Watchlist Category",
    WATCHLIST_LINKED_PEPS: "Watchlist Linked PEPs",
    WATCHLIST_INACTIVE_AS_OF: "Watchlist Inactive As Of",
    WATCHLIST_COOLING_OFF_STATUS: "Watchlist Cooling-Off Status",
    WATCHLIST_DJ_LAST_UPDATED: "Watchlist DJ Last Updated",
    WATCHLIST_SUMMARY: "Watchlist Summary"
} as const;

// ---------------------------------------------------------------------------
// Helpers — small client-side mirrors of backend formatting utilities so the
// page can compute display strings without a round-trip.
// ---------------------------------------------------------------------------

// Renders a reason as a single-line human-readable string.
// Mirrors backend `formatAmlMatchReason`.
export function formatAmlMatchReason(reason: AmlMatchReason): string {
    const namePart = reason.name ? ` - ${reason.name}` : '';
    const pctPart = reason.percentageMatch ? ` (${reason.percentageMatch}%)` : '';
    const catPart = reason.matchCategory ? ` [${reason.matchCategory}]` : '';
    const checkPart = reason.checkName || reason.matchCategory || 'AML alert';
    return `${checkPart}${namePart}${pctPart}${catPart}`;
}

// Coalesces all categorisations into a human label, e.g.
// "PEP (Category One) > Regional Government Ministers (Category Two)".
export function formatCategorisations(report: Pick<WatchlistFullReport, 'categorisations' | 'category' | 'categoryType'>): string {
    if (report.categorisations && report.categorisations.length) {
        return report.categorisations
            .map(c => c.category + (c.categoryType ? ` (${c.categoryType})` : ''))
            .filter(Boolean)
            .join(' > ');
    }
    return report.category || '-';
}

// Renders a CoolingOffStatus as a short badge label, or null if not applicable.
// Tri-state on `pastCoolingOff` per backend contract:
//   - true  -> past cooling-off (ended <date>)
//   - false -> still in cooling-off (until <date>)
//   - null  -> no inactive-as-of date on record
export function formatCoolingOffLabel(coolingOff?: CoolingOffStatus | null): string | null {
    if (!coolingOff || !coolingOff.inactiveAsOfDate) return null;
    if (coolingOff.pastCoolingOff === true) return `Past cooling-off (ended ${coolingOff.coolingOffEndsOn})`;
    if (coolingOff.pastCoolingOff === false) return `In cooling-off until ${coolingOff.coolingOffEndsOn}`;
    return null;
}
